import {
  AllergenIcon, AllergyWarning, Badge, Button, Card, ChildAvatar, ErrorState, Grid, LoadingState, Notice, PageHeader,
  Permissions, PhotoPicker, Row, Stack, StatusBadge, Textarea, usePermission, useApi, useI18n,
  type AllergySeverity, type ChildDetailResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AttendanceHistoryCard } from '../components/AttendanceCards';
import { ChildDetailsCard, TransferCard } from '../components/ChildDetailsCard';
import { ConsentsCard } from '../components/ConsentsCard';
import { ExportsCard } from '../components/ExportsCard';
import { GuardiansCard } from '../components/GuardiansCard';
import { LegalHoldCard } from '../components/LegalHoldCard';
import { ReenrollCard } from '../components/ReenrollCard';
import { RestrictionsCard } from '../components/RestrictionsCard';
import { HealthRecordCard } from '../components/HealthRecordCard';
import { IncidentsCard } from '../components/IncidentsCard';
import { MedicationsCard } from '../components/MedicationsCard';
import { PickupsCard } from '../components/PickupsCard';
import { AllergiesEditor, ContactsEditor } from '../components/RegistrationEditors';
import {
  allergiesToRequest, contactsToRequest, emptyContact,
  type AllergyDraft, type ContactDraft,
} from '../components/registrationDrafts';
import { Age } from './Children';

const SEVERITY_TONE: Record<AllergySeverity, 'neutral' | 'warning' | 'danger'> = {
  Mild: 'neutral', Severe: 'danger', Anaphylactic: 'danger',
};

/**
 * A child's profile: identity, health and emergency contacts (SRS §4.12). Keyed by the child, so moving from one
 * child to another starts the page afresh: a notice or a half-written withdrawal reason never follows onto the next.
 */
export function Child() {
  const { id } = useParams();
  return <ChildProfile key={id} id={id} />;
}

function ChildProfile({ id }: { id: string | undefined }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatDate } = useI18n();
  const canWithdraw = usePermission(Permissions.children.withdraw);
  const canReenroll = usePermission(Permissions.children.create);
  const canUpdate = usePermission(Permissions.children.update);
  // The nurse keeps the allergies current as well (decision 2026-10-04 #2); the API takes either.
  const canEditAllergies = usePermission(Permissions.health.manage) || canUpdate;
  // Set by the new-child form when the child was registered but the photo did not upload.
  const photoFailed = (useLocation().state as { photoFailed?: boolean } | null)?.photoFailed === true;

  const childId = Number(id);
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  // Set once the enrolment ends here, so the copies card offers each guardian a copy (Q51).
  const [justWithdrawn, setJustWithdrawn] = useState(false);

  const child = useQuery({
    queryKey: ['child', childId],
    queryFn: () => api.children.get(childId),
    enabled: Number.isFinite(childId),
  });

  const withdraw = useMutation({
    mutationFn: () => api.children.withdraw(childId, reason.trim() || null, null),
    // The answer is empty: the profile is read again, and so are its guardians, whose links the withdrawal ended.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['child', childId] });
      void queryClient.invalidateQueries({ queryKey: ['children'] });
      setConfirming(false);
      setJustWithdrawn(true);
    },
  });

  // The photo endpoints answer with the new link only, so the cached profile is patched rather than
  // refetched, and the list is marked stale so its avatar follows.
  const storePhoto = (photoUrl: string | null) => {
    queryClient.setQueryData<ChildDetailResponse>(['child', childId], (current) => current && { ...current, photoUrl });
    void queryClient.invalidateQueries({ queryKey: ['children'] });
  };

  if (child.isPending) return <LoadingState />;
  if (child.isError) return <ErrorState error={child.error} onRetry={() => void child.refetch()} />;

  const data = child.data;
  const name = data.preferredName
    ? `${data.preferredName} ${data.lastName}`
    : `${data.firstName} ${data.lastName}`;

  return (
    <>
      <PageHeader
        title={name}
        lede={`${data.firstName} ${data.lastName}`}
        actions={<Link className="dg-button dg-button--ghost" to="/children">{t('action.back')}</Link>}
      />

      <Row>
        <ChildAvatar child={data} photoUrl={data.photoUrl} allergies={data.allergies} />
        <StatusBadge status={data.status} />
        {data.className && <Badge>{data.className}</Badge>}
        <Badge tone="neutral">{t(`gender.${data.gender}`)}</Badge>
      </Row>

      {/* Before anything else on the page: whoever opened this record may be about to feed the child. */}
      <AllergyWarning allergies={data.allergies} />

      <Grid>
        <Card title={t('children.column.dob')}>
          <Stack tight>
            <strong>{formatDate(data.dateOfBirth)}</strong>
            <span className="dg-card__hint"><Age dateOfBirth={data.dateOfBirth} /></span>
          </Stack>
        </Card>
        <Card title={t('child.enrolled')}>
          <Stack tight>
            <strong>{formatDate(data.enrollmentDate)}</strong>
            {data.withdrawalDate && (
              <span className="dg-card__hint">
                {t('child.withdraw')}: {formatDate(data.withdrawalDate)}
                {data.withdrawalReason ? ` — ${data.withdrawalReason}` : ''}
              </span>
            )}
          </Stack>
        </Card>
      </Grid>

      <ChildDetailsCard child={data} />
      <TransferCard child={data} />

      {canUpdate && (
        <Card title={t('child.photo')}>
          <Stack>
            {photoFailed && !data.photoUrl && <Notice tone="warning">{t('children.new.photoFailed')}</Notice>}
            <PhotoPicker
              name={data.preferredName || data.firstName}
              photoUrl={data.photoUrl}
              hint={t('child.photo.hint')}
              onSave={(photo) => api.children.setPhoto(childId, photo).then((result) => storePhoto(result.photoUrl ?? null))}
              onRemove={() => api.children.removePhoto(childId).then(() => storePhoto(null))}
            />
          </Stack>
        </Card>
      )}

      <AllergiesCard child={data} canUpdate={canEditAllergies} />

      <GuardiansCard childId={childId} enrollmentDate={data.enrollmentDate} />
      <PickupsCard childId={childId} />
      <RestrictionsCard childId={childId} />

      <ConsentsCard childId={childId} />

      <HealthRecordCard childId={childId} />

      <MedicationsCard childId={childId} />

      <IncidentsCard childId={childId} />

      <AttendanceHistoryCard childId={childId} />

      <ExportsCard childId={childId} justWithdrawn={justWithdrawn} />

      <LegalHoldCard childId={childId} />

      <ContactsCard child={data} canUpdate={canUpdate} />

      {canWithdraw && data.status === 'Active' && (
        <Card title={t('child.withdraw')}>
          <Stack>
            {withdraw.isError && <ErrorState error={withdraw.error} />}
            {confirming ? (
              <>
                <Notice tone="warning">{t('child.withdraw.hint')}</Notice>
                <Notice tone="info">{t('child.withdraw.copies')}</Notice>
                <label className="dg-field">
                  <span className="dg-field__label">{t('child.withdraw.reason')}</span>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                </label>
                <Row>
                  <Button
                    variant="danger"
                    busy={withdraw.isPending}
                    busyLabel={t('action.saving')}
                    onClick={() => withdraw.mutate()}
                  >
                    {t('child.withdraw.confirm')}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirming(false)}>{t('action.cancel')}</Button>
                </Row>
              </>
            ) : (
              <Row>
                <Button variant="danger" onClick={() => setConfirming(true)}>{t('child.withdraw')}</Button>
              </Row>
            )}
          </Stack>
        </Card>
      )}

      {canReenroll && data.status === 'Withdrawn' && <ReenrollCard childId={childId} />}
    </>
  );
}

/** Patches the cached profile after an edit, and marks the registration-gaps list stale. */
function useRefreshChild(childId: number) {
  const api = useApi();
  const queryClient = useQueryClient();
  return async () => {
    queryClient.setQueryData(['child', childId], await api.children.get(childId));
    void queryClient.invalidateQueries({ queryKey: ['registration-gaps'] });
  };
}

/** The edit cycle both registration cards share: open seeded from the record, save, refresh, close. */
function useEditableCard(childId: number, saveFn: () => Promise<unknown>) {
  const refresh = useRefreshChild(childId);
  const [editing, setEditing] = useState(false);
  const save = useMutation({
    mutationFn: saveFn,
    onSuccess: async () => { await refresh(); setEditing(false); },
  });

  return {
    editing,
    open(seed: () => void) {
      seed();
      save.reset();
      setEditing(true);
    },
    footer: <EditFooter busy={save.isPending} error={save.error} onSave={() => save.mutate()} onCancel={() => setEditing(false)} />,
  };
}

/** The allergy answer (Q9): the allergies, the family's "none known", or a warning that nobody has answered. */
function AllergiesCard({ child, canUpdate }: { child: ChildDetailResponse; canUpdate: boolean }) {
  const api = useApi();
  const { t } = useI18n();
  const [allergies, setAllergies] = useState<AllergyDraft[]>([]);
  const [noneKnown, setNoneKnown] = useState(false);
  const card = useEditableCard(child.id, () => api.children.replaceAllergies(
    child.id, noneKnown ? null : allergiesToRequest(allergies), noneKnown));

  const edit = () => card.open(() => {
    setAllergies(child.allergies.map((a) => ({
      allergen: a.allergen, severity: a.severity, reaction: a.reaction ?? '', actionRequired: a.actionRequired ?? '',
    })));
    setNoneKnown(child.noKnownAllergies);
  });

  if (card.editing) {
    return (
      <AllergiesEditor
        title={t('child.allergies')}
        allergies={allergies}
        setAllergies={setAllergies}
        noneKnown={noneKnown}
        setNoneKnown={setNoneKnown}
        footer={card.footer}
      />
    );
  }

  return (
    <Card
      title={t('child.allergies')}
      action={canUpdate && <Button variant="secondary" onClick={edit}>{t('child.edit')}</Button>}
    >
      {child.allergies.length > 0 ? (
        <Stack>
          {child.allergies.map((allergy) => (
            <div key={allergy.id} className="dg-stack dg-stack--tight">
              <Row>
                <AllergenIcon icon={allergy.icon} allergen={allergy.allergen} severity={allergy.severity} />
                <strong>{allergy.allergen}</strong>
                <Badge tone={SEVERITY_TONE[allergy.severity]}>
                  {t(`child.severity.${allergy.severity}`)}
                </Badge>
              </Row>
              {allergy.reaction && <span className="dg-card__hint">{allergy.reaction}</span>}
              {allergy.actionRequired && <span>{allergy.actionRequired}</span>}
            </div>
          ))}
        </Stack>
      ) : child.noKnownAllergies
        ? <p className="dg-card__hint">{t('child.allergies.noneKnown')}</p>
        : <Notice tone="warning">{t('child.allergies.unanswered')}</Notice>}
    </Card>
  );
}

/** Who to call, in calling order, or a warning that nobody is on record (Q9). */
function ContactsCard({ child, canUpdate }: { child: ChildDetailResponse; canUpdate: boolean }) {
  const api = useApi();
  const { t, formatPhone } = useI18n();
  const [contacts, setContacts] = useState<ContactDraft[]>([]);
  // Ordered by the priority the nursery set, so the first number to ring is the first read.
  const ordered = [...child.emergencyContacts].sort((a, b) => a.priority - b.priority);
  const card = useEditableCard(child.id, () => api.children.replaceEmergencyContacts(child.id, contactsToRequest(contacts)));

  const edit = () => card.open(() => setContacts(ordered.length > 0
    ? ordered.map((c) => ({ name: c.name, relationship: c.relationship ?? '', phone: c.phone }))
    : [emptyContact()]));

  if (card.editing) {
    return (
      <ContactsEditor
        title={t('child.emergencyContacts')}
        contacts={contacts}
        setContacts={setContacts}
        footer={card.footer}
      />
    );
  }

  return (
    <Card
      title={t('child.emergencyContacts')}
      action={canUpdate && <Button variant="secondary" onClick={edit}>{t('child.edit')}</Button>}
    >
      {ordered.length === 0
        ? <Notice tone="warning">{t('child.emergencyContacts.none')}</Notice>
        : (
          <Stack>
            {ordered.map((contact) => (
              <Row key={contact.id} between>
                <span className="dg-stack dg-stack--tight">
                  <strong>{contact.name}</strong>
                  {contact.relationship && <span className="dg-card__hint">{contact.relationship}</span>}
                </span>
                <a href={`tel:${contact.phone}`} dir="ltr">{formatPhone(contact.phone)}</a>
              </Row>
            ))}
          </Stack>
        )}
    </Card>
  );
}

/** Save and cancel under an editor, with the API's refusal above them in its own words. */
function EditFooter({ busy, error, onSave, onCancel }: {
  busy: boolean; error: Error | null; onSave: () => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  return (
    <>
      {error && <ErrorState error={error} />}
      <Row>
        <Button busy={busy} busyLabel={t('action.saving')} onClick={onSave}>{t('action.save')}</Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>{t('action.cancel')}</Button>
      </Row>
    </>
  );
}
